import { useCallback } from 'react'

import { ResponseContext } from '@cash-web/protos/squareup/franklin/ResponseContextProto.pb'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { getBlockerDataDetail } from '@cash-web/shared-flow-data-access-context'
import { CashResponse, CashResponseContext, CashResponseMiddleware } from '@cash-web/shared-util-fetch'
import { BlockerFlowInteractReceiveJourneyOutcome } from '@squareup/customer-data-events-web'

import { FlowState } from '../../hooks/flowReducer'
import { getAnalyticsClientScenario } from '../../hooks/useFlowLogging'

/**
 * Hook that creates a middleware that logs CDF events when journey outcomes are received in flow responses.
 * This logs BlockerFlowInteractReceiveJourneyOutcome events for analytics tracking.
 */
export const useCreateCdfJourneyOutcomeResponseMiddleware = ({
  state,
}: {
  state: FlowState
}): CashResponseMiddleware => {
  const logCdfEvent = useLogCashWebCdfEvent()

  const analyticsClientScenario = getAnalyticsClientScenario(state)

  return useCallback(
    (response: CashResponse<{ response_context?: ResponseContext }>, context?: CashResponseContext) => {
      const journeyOutcome = response?.response_context?.scenario_plan?.journey_outcome

      // Only log if there's a journey outcome in the response
      if (journeyOutcome) {
        // For no-blocker flows, this response can be processed before FLOW_START updates state, so use the request-scoped identity when state has not caught up.
        const useRequestIdentity = state.clientScenario == null && context?.options?.clientScenario != null
        const clientScenario = useRequestIdentity ? context.options?.clientScenario : analyticsClientScenario
        const flowToken = useRequestIdentity ? (context.options?.flowToken ?? state.flowToken) : state.flowToken
        // Pass context with different headers to ensure state is used instead
        const blockerData = state.activeBlockerDescriptor
          ? getBlockerDataDetail(state.activeBlockerDescriptor)
          : undefined

        logCdfEvent(
          BlockerFlowInteractReceiveJourneyOutcome({
            clientScenario,
            flowToken,
            originatingFlowType: state.originatingFlowType,
            lastBlockerId: blockerData?.id,
            lastBlockerType: blockerData?.type,
            outcome: journeyOutcome,
          })
        )
      }

      return response
    },
    [
      logCdfEvent,
      state.activeBlockerDescriptor,
      state.clientScenario,
      state.flowToken,
      state.originatingFlowType,
      analyticsClientScenario,
    ]
  )
}
